/**
 * SupersetContainer - Visual container for grouped exercises (supersets, tri-sets, giant sets)
 * Renders grouped exercises with accent bar, header label, and dissolution control.
 * Supports internal drag-to-reorder via nested SortableContext.
 */

import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bars2Icon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { GROUP_TYPE_LABELS } from '@personal-trainer-app/shared/src/utils/exercise-groups';

import type {
  SortableExerciseProps,
  SupersetContainerProps,
} from './SupersetContainer.types';
import type { SortableItemContext } from '@/components/ui';
import type { DragEndEvent, DragMoveEvent } from '@dnd-kit/core';

import { IconButton } from '@/components/ui';
import { useMergedRef } from '@/hooks';

const SortableExercise = memo(function SortableExercise({
  exercise,
  renderExercise,
  disabled,
}: SortableExerciseProps): React.JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: exercise.uuid, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const context: SortableItemContext = {
    isDragging,
    isOverlay: false,
    handleAttributes: attributes,
    handleListeners: listeners,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {renderExercise(exercise, context)}
    </div>
  );
});

export const SupersetContainer = forwardRef<
  HTMLDivElement,
  SupersetContainerProps
>(function SupersetContainer(
  {
    groupLeaderUuid,
    groupType,
    exercises,
    isSelected = false,
    isDropTarget = false,
    onDissolve,
    onExerciseReorder,
    onExerciseDragOut,
    renderExercise,
    className = '',
    isDragging = false,
    isOverlay = false,
    dragHandleAttributes,
    dragHandleListeners,
  },
  forwardedRef
) {
  const label = GROUP_TYPE_LABELS[groupType];
  const instructionsId = useId();
  const isInitialMount = useRef(true);
  const internalContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useMergedRef(forwardedRef, internalContainerRef);
  const [hasAppearAnimation, setHasAppearAnimation] = useState(true);
  const [activeExerciseId, setActiveExerciseId] = useState<string | null>(null);
  const [isLeavingGroup, setIsLeavingGroup] = useState(false);
  const lastPointerYRef = useRef<number>(0);

  // Sensors for internal drag-to-reorder (shorter activation for nested context)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback(
    (event: { active: { id: string | number } }) => {
      setActiveExerciseId(String(event.active.id));
    },
    []
  );

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      if (!internalContainerRef.current || !activeExerciseId) return;

      // Calculate actual pointer Y from the original event + accumulated delta
      // This bypasses dnd-kit's internal rect calculations which are unreliable in nested contexts
      const activatorEvent = event.activatorEvent;
      let pointerY: number;

      if (activatorEvent instanceof MouseEvent) {
        pointerY = activatorEvent.clientY + event.delta.y;
      } else if (activatorEvent instanceof TouchEvent) {
        const touch = activatorEvent.touches[0];
        if (!touch) return;
        pointerY = touch.clientY + event.delta.y;
      } else {
        return;
      }

      // Store for use in handleDragEnd
      lastPointerYRef.current = pointerY;

      const containerRect =
        internalContainerRef.current.getBoundingClientRect();
      const tolerance = 30; // px outside container to trigger leaving state

      const isOutside =
        pointerY < containerRect.top - tolerance ||
        pointerY > containerRect.bottom + tolerance;

      setIsLeavingGroup(isOutside);
    },
    [activeExerciseId]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      const wasLeavingGroup = isLeavingGroup;

      setActiveExerciseId(null);
      setIsLeavingGroup(false);

      // Handle drag-out: exercise left container bounds
      if (wasLeavingGroup && !over && onExerciseDragOut) {
        // Use the stored pointer Y from handleDragMove (actual pointer position)
        onExerciseDragOut(String(active.id), lastPointerYRef.current);
        return;
      }

      // Existing: reorder within group
      if (over && active.id !== over.id && onExerciseReorder) {
        const oldIndex = exercises.findIndex((e) => e.uuid === active.id);
        const newIndex = exercises.findIndex((e) => e.uuid === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const reordered = arrayMove(exercises, oldIndex, newIndex);
          onExerciseReorder(reordered.map((e) => e.uuid));
        }
      }
    },
    [exercises, onExerciseReorder, isLeavingGroup, onExerciseDragOut]
  );

  const handleDragCancel = useCallback(() => {
    setActiveExerciseId(null);
    setIsLeavingGroup(false);
  }, []);

  const activeExercise = exercises.find((e) => e.uuid === activeExerciseId);
  const exerciseIds = exercises.map((e) => e.uuid);
  const canReorder = Boolean(onExerciseReorder) && !isOverlay;

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      const timer = setTimeout(() => setHasAppearAnimation(false), 200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="superset-container"
      data-group-type={groupType}
      data-group-leader={groupLeaderUuid}
      className={`
        relative rounded-lg p-2 mb-2 transition-all duration-200
        ${hasAppearAnimation ? 'animate-superset-appear' : ''}
        ${
          isSelected
            ? 'border-[1.5px] border-stroke-accent bg-sakura-wash'
            : 'border border-stroke-subtle bg-lift-1'
        }
        ${isDropTarget ? 'ring-2 ring-primary-400/50' : ''}
        ${isLeavingGroup ? 'ring-2 ring-warning-400/50' : ''}
        ${isDragging && !isOverlay ? 'opacity-40 border-dashed' : ''}
        ${isOverlay ? 'shadow-lift-popover ring-1 ring-primary-300/30 rotate-[-0.5deg]' : ''}
        ${className}
      `}
      role="group"
      aria-label={`${label} with ${exercises.map((e) => e.name).join(', ')}`}
      aria-describedby={instructionsId}
    >
      {/* Screen reader instructions */}
      <span id={instructionsId} className="sr-only">
        {exercises.length} exercises performed back-to-back. Drag exercises to
        reorder within group.
      </span>
      {/* Left accent bar */}
      <div
        className={`
          absolute left-[-2px] top-2 bottom-2 w-[3px] rounded-full
          ${isSelected ? 'bg-primary-300' : 'bg-stroke-strong'}
        `}
        aria-hidden="true"
      />

      {/* Header with drag handle, label, and dissolve button */}
      <div className="flex items-center justify-between px-1 py-0.5 mb-1">
        <div className="flex items-center gap-1">
          {/* Drag handle for reordering the entire group */}
          {dragHandleAttributes && dragHandleListeners && (
            <button
              {...dragHandleAttributes}
              {...dragHandleListeners}
              className="p-0.5 -ml-1 text-neutral-500 hover:text-neutral-300 cursor-grab active:cursor-grabbing touch-none"
              aria-label={`Drag to reorder ${label.toLowerCase()}`}
            >
              <Bars2Icon className="w-4 h-4" />
            </button>
          )}
          <span
            className={`
              text-label-xs font-semibold tracking-ultra
              ${isSelected ? 'text-primary-300' : 'text-neutral-400'}
            `}
          >
            {label}
          </span>
        </div>
        <IconButton
          data-testid="dissolve-superset-button"
          variant="ghost"
          size="xs"
          icon={<XMarkIcon />}
          aria-label={`Dissolve ${label.toLowerCase()}`}
          onClick={() => onDissolve(groupLeaderUuid)}
          className="!text-neutral-500 hover:!text-neutral-300 hover:!bg-lift-3 -mr-1"
        />
      </div>

      {/* Exercise list with internal drag-to-reorder */}
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={exerciseIds}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-1">
            {exercises.map((exercise) => (
              <SortableExercise
                key={exercise.uuid}
                exercise={exercise}
                renderExercise={renderExercise}
                disabled={!canReorder}
              />
            ))}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeExercise
            ? renderExercise(activeExercise, {
                isDragging: true,
                isOverlay: true,
                handleAttributes: undefined,
                handleListeners: undefined,
              })
            : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
});

export default SupersetContainer;
